import { gql } from '@apollo/client'
import type { CustomerDataApiDeployment, DeploymentStatus } from '@helo/shared'
import { parse } from 'graphql'

import { apolloClient } from './apolloClient'

// Shared GraphQL fragments so the deployment shape stays consistent across
// list / by-id / rerun / cancel operations.
const deploymentFieldsFragment = gql`
  fragment DeploymentFields on CustomerDataApiDeployment {
    id
    configId
    attempt
    status
    isActive
    logs
    startedAt
    completedAt
    canceledAt
    canceledByUserId
    canceledByUser {
      email
      name
    }
    lastProgressAt
    deployedByUserId
    deployedByUser {
      email
      name
    }
    smrtVersions {
      subgraphName
      smrtVersion
      smrtIri
    }
    config {
      id
      createdDate
    }
  }
`

const subgraphConfigFieldsFragment = gql`
  fragment SubgraphConfigFields on SubgraphConfig {
    id
    subgraphName
    path
    customerDataSources {
      id
      organizationName
      currentVersion
      isMainIntrospectionDB
      customerDatabases {
        id
        version
        host
        port
        databaseName
        cubeApiUrl
      }
    }
  }
`

export const customerDataApiConfigQuery = gql`
  query CustomerDataApiConfig {
    customerDataApiConfig {
      id
      createdDate
      updatedDate
      isActive
      lastUpdatedByUser
      lastUpdatedBy {
        name
        email
      }
      latestDeployment {
        ...DeploymentFields
      }
      subgraphConfigs {
        ...SubgraphConfigFields
      }
    }
  }
  ${deploymentFieldsFragment}
  ${subgraphConfigFieldsFragment}
`

export const customerDataApiConfigsQuery = gql`
  query CustomerDataApiConfigs($first: Int, $skip: Int) {
    CustomerDataApiConfig_list(
      first: $first
      skip: $skip
      orderBy: [{ updatedDate: DESC }]
    ) {
      items {
        id
        createdDate
        updatedDate
        isActive
        lastUpdatedByUser
        lastUpdatedBy {
          name
          email
        }
        latestDeployment {
          ...DeploymentFields
        }
        subgraphConfigs {
          ...SubgraphConfigFields
        }
      }
    }
  }
  ${deploymentFieldsFragment}
  ${subgraphConfigFieldsFragment}
`

export const customerDataApiConfigByIdQuery = gql`
  query CustomerDataApiConfigById($id: String!) {
    customerDataApiConfigById(id: $id) {
      id
      createdDate
      updatedDate
      isActive
      lastUpdatedByUser
      lastUpdatedBy {
        name
        email
      }
      latestDeployment {
        ...DeploymentFields
      }
      deployments {
        ...DeploymentFields
      }
      subgraphConfigs {
        ...SubgraphConfigFields
      }
    }
  }
  ${deploymentFieldsFragment}
  ${subgraphConfigFieldsFragment}
`

export const createCustomerDataApiConfigMutation = gql`
  mutation CreateCustomerDataApiConfig(
    $input: CreateCustomerDataApiConfigInput!
  ) {
    createCustomerDataApiConfig(input: $input) {
      id
      createdDate
      updatedDate
      isActive
      lastUpdatedByUser
      latestDeployment {
        id
        status
      }
      subgraphConfigs {
        ...SubgraphConfigFields
      }
    }
  }
  ${subgraphConfigFieldsFragment}
`

const updateCustomerDataApiConfigMutation = gql`
  mutation UpdateCustomerDataApiConfig(
    $input: UpdateCustomerDataApiConfigInput!
  ) {
    updateCustomerDataApiConfig(input: $input) {
      id
      createdDate
      updatedDate
      isActive
      lastUpdatedByUser
      latestDeployment {
        id
        status
      }
      subgraphConfigs {
        ...SubgraphConfigFields
      }
    }
  }
  ${subgraphConfigFieldsFragment}
`

/**
 * API input types only allow id + subgraphConfigs (update) or subgraphConfigs (create).
 * Build a new object with only these keys so isActive, lastUpdatedByUser, etc. are never sent.
 */
const createConfigInput = (raw: any) => ({
  subgraphConfigs: raw?.subgraphConfigs ?? [],
})
const updateConfigInput = (raw: any) => ({
  id: raw?.id ?? '',
  subgraphConfigs: raw?.subgraphConfigs ?? [],
})

export const createCustomerDataApiConfig = async (input: any) => {
  const result = await apolloClient.mutate({
    mutation: createCustomerDataApiConfigMutation,
    variables: { input: createConfigInput(input) },
  })
  return (result.data as any)?.createCustomerDataApiConfig
}

export const getCustomerDataApiConfig = async () => {
  const result = await apolloClient.query({
    query: customerDataApiConfigQuery,
    fetchPolicy: 'network-only',
  })
  return (result.data as any)?.customerDataApiConfig
}

export const updateCustomerDataApiConfig = async (input: any) => {
  const result = await apolloClient.mutate({
    mutation: updateCustomerDataApiConfigMutation,
    variables: { input: updateConfigInput(input) },
  })
  return (result.data as any)?.updateCustomerDataApiConfig
}

export const getCustomerDataApiConfigs = async () => {
  const result = await apolloClient.query({
    query: customerDataApiConfigsQuery,
    variables: { first: 200, skip: 0 },
    fetchPolicy: 'network-only',
  })
  return ((result.data as any)?.CustomerDataApiConfig_list?.items ??
    []) as any[]
}

export const getCustomerDataApiConfigById = async (id: string) => {
  const result = await apolloClient.query({
    query: customerDataApiConfigByIdQuery,
    variables: { id },
    fetchPolicy: 'network-only',
  })
  return (result.data as any)?.customerDataApiConfigById ?? null
}

export type { CustomerDataApiDeployment, DeploymentStatus }

export type CustomerDataApiServiceStatus = {
  name: string
  accessible: boolean
  status?: string | null
  message?: string | null
  error?: string | null
}

export type CustomerDataApiStatusDeploymentRef = {
  id: string
  configId: string
  status: DeploymentStatus
  attempt: number
  startedAt: string
  completedAt?: string | null
}

export type CustomerDataApiStatusArtifactRef = {
  id: string
  subgraphId: string
  subgraphName: string
  smrtIri: string
  smrtVersion: string
  schemaId?: string | null
  resolvedFrom?: string | null
  introspectionHost?: string | null
}

export type CustomerDataApiRuntimeHealthCheck = {
  accessible: boolean
  message?: string | null
  error?: string | null
  responseTimeMs?: number | null
}

export type CustomerDataApiRuntimeCustomer = {
  organizationName: string
  currentVersion?: string | null
  databaseName?: string | null
  host?: string | null
  port?: number | null
  status: string
  isDisabled: boolean
  database: CustomerDataApiRuntimeHealthCheck
  cube: {
    configured: boolean
    apiUrl?: string | null
    reachability: CustomerDataApiRuntimeHealthCheck
    introspection: CustomerDataApiRuntimeHealthCheck
  }
}

export type CustomerDataApiRuntimeSubgraph = {
  id: string
  subgraphName: string
  path: string
  status: string
  isDisabled: boolean
  createdAt?: string | null
  recentLogs: string[]
  customers: CustomerDataApiRuntimeCustomer[]
}

export type CustomerDataApiManagerRuntime = {
  managerAccessible: boolean
  subgraphsServiceRunning: boolean
  status?: string | null
  error?: string | null
  pipeline: {
    running: boolean
    deploymentId?: string | null
  }
  subgraphs: CustomerDataApiRuntimeSubgraph[]
}

export type CustomerDataApiStatus = {
  checkedAt: string
  manager: CustomerDataApiServiceStatus
  subgraphsService: CustomerDataApiServiceStatus
  cubeService: CustomerDataApiServiceStatus
  cubeIntrospection: CustomerDataApiServiceStatus
  cache: CustomerDataApiServiceStatus
  liveConfig?: {
    id: string
    createdDate: string
    updatedDate: string
  } | null
  liveDeployment?: CustomerDataApiStatusDeploymentRef | null
  inProgressDeployments: CustomerDataApiStatusDeploymentRef[]
  liveSmrtArtifacts: CustomerDataApiStatusArtifactRef[]
  managerRuntime?: CustomerDataApiManagerRuntime | null
}

export type CustomerDataApiControlResult = {
  success: boolean
  message: string
  deploymentId?: string | null
}

export const customerDataApiStatusQuery = parse(`
  query CustomerDataApiStatus {
    customerDataApiStatus {
      checkedAt
      manager {
        name
        accessible
        status
        message
        error
      }
      subgraphsService {
        name
        accessible
        status
        message
        error
      }
      cubeService {
        name
        accessible
        status
        message
        error
      }
      cubeIntrospection {
        name
        accessible
        status
        message
        error
      }
      cache {
        name
        accessible
        status
        message
        error
      }
      liveConfig {
        id
        createdDate
        updatedDate
      }
      liveDeployment {
        id
        configId
        status
        attempt
        startedAt
        completedAt
      }
      inProgressDeployments {
        id
        configId
        status
        attempt
        startedAt
        completedAt
      }
      liveSmrtArtifacts {
        id
        subgraphId
        subgraphName
        smrtIri
        smrtVersion
        schemaId
        resolvedFrom
        introspectionHost
      }
      managerRuntime
    }
  }
`)

export const reloadCustomerDataApiRuntimeMutation = parse(`
  mutation ReloadCustomerDataApiRuntime {
    reloadCustomerDataApiRuntime {
      success
      message
      deploymentId
    }
  }
`)

export const clearCustomerDataApiRuntimeCacheMutation = parse(`
  mutation ClearCustomerDataApiRuntimeCache {
    clearCustomerDataApiRuntimeCache {
      success
      message
      deploymentId
    }
  }
`)

export const getCustomerDataApiStatus =
  async (): Promise<CustomerDataApiStatus> => {
    const result = await apolloClient.query({
      query: customerDataApiStatusQuery,
      fetchPolicy: 'network-only',
    })
    return (result.data as any)?.customerDataApiStatus as CustomerDataApiStatus
  }

export const reloadCustomerDataApiRuntime =
  async (): Promise<CustomerDataApiControlResult> => {
    const result = await apolloClient.mutate({
      mutation: reloadCustomerDataApiRuntimeMutation,
    })
    return (result.data as any)
      ?.reloadCustomerDataApiRuntime as CustomerDataApiControlResult
  }

export const clearCustomerDataApiRuntimeCache =
  async (): Promise<CustomerDataApiControlResult> => {
    const result = await apolloClient.mutate({
      mutation: clearCustomerDataApiRuntimeCacheMutation,
    })
    return (result.data as any)
      ?.clearCustomerDataApiRuntimeCache as CustomerDataApiControlResult
  }

export const customerDataApiDeploymentsQuery = gql`
  query CustomerDataApiDeployments(
    $first: Int
    $skip: Int
    $filter: CustomerDataApiDeploymentFilterInput
    $orderBy: [CustomerDataApiDeploymentOrderByInput!]
  ) {
    CustomerDataApiDeployment_list(
      first: $first
      skip: $skip
      filter: $filter
      orderBy: $orderBy
    ) {
      items {
        ...DeploymentFields
      }
    }
  }
  ${deploymentFieldsFragment}
`

export const customerDataApiDeploymentByIdQuery = gql`
  query CustomerDataApiDeploymentById($id: String!) {
    customerDataApiDeploymentById(id: $id) {
      ...DeploymentFields
      config {
        id
        createdDate
        subgraphConfigs {
          ...SubgraphConfigFields
        }
      }
    }
  }
  ${deploymentFieldsFragment}
  ${subgraphConfigFieldsFragment}
`

export const rerunCustomerDataApiDeploymentMutation = gql`
  mutation RerunCustomerDataApiDeployment($deploymentId: String!) {
    rerunCustomerDataApiDeployment(deploymentId: $deploymentId) {
      ...DeploymentFields
    }
  }
  ${deploymentFieldsFragment}
`

export const cancelCustomerDataApiDeploymentMutation = gql`
  mutation CancelCustomerDataApiDeployment($deploymentId: String!) {
    cancelCustomerDataApiDeployment(deploymentId: $deploymentId) {
      ...DeploymentFields
    }
  }
  ${deploymentFieldsFragment}
`

export const getCustomerDataApiDeployments = async (options?: {
  first?: number
  skip?: number
  filter?: Record<string, unknown>
  orderBy?: Record<string, 'ASC' | 'DESC'>[]
}): Promise<CustomerDataApiDeployment[]> => {
  const result = await apolloClient.query({
    query: customerDataApiDeploymentsQuery,
    variables: {
      first: options?.first ?? 50,
      skip: options?.skip ?? 0,
      filter: options?.filter,
      orderBy: options?.orderBy ?? [{ startedAt: 'DESC' }],
    },
    fetchPolicy: 'network-only',
  })
  return ((result.data as any)?.CustomerDataApiDeployment_list?.items ??
    []) as CustomerDataApiDeployment[]
}

export const getCustomerDataApiDeploymentById = async (
  id: string,
): Promise<CustomerDataApiDeployment | null> => {
  const result = await apolloClient.query({
    query: customerDataApiDeploymentByIdQuery,
    variables: { id },
    fetchPolicy: 'network-only',
  })
  return ((result.data as any)?.customerDataApiDeploymentById ??
    null) as CustomerDataApiDeployment | null
}

export const rerunCustomerDataApiDeployment = async (
  deploymentId: string,
): Promise<CustomerDataApiDeployment> => {
  const result = await apolloClient.mutate({
    mutation: rerunCustomerDataApiDeploymentMutation,
    variables: { deploymentId },
  })
  return (result.data as any)
    ?.rerunCustomerDataApiDeployment as CustomerDataApiDeployment
}

export const cancelCustomerDataApiDeployment = async (
  deploymentId: string,
): Promise<CustomerDataApiDeployment> => {
  const result = await apolloClient.mutate({
    mutation: cancelCustomerDataApiDeploymentMutation,
    variables: { deploymentId },
  })
  return (result.data as any)
    ?.cancelCustomerDataApiDeployment as CustomerDataApiDeployment
}
